<template>
  <div class="page-container reveal pb-12">
    <!-- En-tête de page responsive -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
      <div>
        <h2 class="text-xl sm:text-2xl font-black text-profissa-text-main flex items-center gap-2.5">
          <el-icon class="text-profissa-accent"><Tools /></el-icon>
          <span>Paramètres de la Plateforme</span>
        </h2>
        <p class="text-xs sm:text-sm text-profissa-text-dim mt-1">
          Gestion dynamique des constantes métier, tarifs, seuils GPS et maintenance
        </p>
      </div>
      <div class="flex items-center gap-2 self-start sm:self-auto">
        <el-button
          :icon="Refresh"
          circle
          :loading="isLoading"
          @click="loadSettings"
          title="Actualiser les paramètres"
        />
      </div>
    </div>

    <!-- Bannière mode consultation si permissions restreintes -->
    <div
      v-if="!canManage"
      class="mb-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs sm:text-sm flex items-start sm:items-center gap-3"
    >
      <el-icon class="text-xl shrink-0 mt-0.5 sm:mt-0"><Warning /></el-icon>
      <div class="leading-relaxed">
        <strong>Mode consultation :</strong> Vous visualisez les paramètres de la plateforme en lecture seule. L'autorisation d'administrateur principal est requise pour appliquer des modifications.
      </div>
    </div>

    <!-- Alerte mode maintenance actif -->
    <div
      v-if="form.maintenance_mode"
      class="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-xs sm:text-sm flex items-start sm:items-center gap-3"
    >
      <el-icon class="text-xl text-red-400 shrink-0 mt-0.5 sm:mt-0"><WarningFilled /></el-icon>
      <div class="leading-relaxed">
        <strong>Mode maintenance actif :</strong> Les applications mobiles bloquent actuellement la création et l'acceptation de missions.
      </div>
    </div>

    <!-- Loader initial -->
    <div v-if="isLoading && !isLoaded" class="flex justify-center p-16">
      <el-icon class="is-loading text-4xl text-profissa-accent"><Loading /></el-icon>
    </div>

    <div v-else class="space-y-6">
      <!-- Ligne 1 : Économie & Géolocalisation -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Carte Économie & Tarification -->
        <el-card class="settings-card !border-profissa-glass-border">
          <template #header>
            <div class="flex items-center gap-2.5">
              <div class="w-8 h-8 rounded-lg bg-profissa-accent/10 border border-profissa-accent/20 flex items-center justify-center text-profissa-accent">
                <el-icon class="text-base"><Coin /></el-icon>
              </div>
              <div>
                <span class="font-bold text-sm sm:text-base text-profissa-text-main block">Économie & Tarification</span>
                <span class="text-[0.7rem] text-profissa-text-dim block">Commissions, séquestre et retraits</span>
              </div>
            </div>
          </template>

          <el-form label-position="top" :disabled="!canManage" class="space-y-4">
            <el-form-item label="Garantie de déplacement / diagnostic (FCFA)">
              <el-input-number
                v-model="form.diagnostic_fee"
                :min="500"
                :max="20000"
                :step="100"
                controls-position="right"
                style="width: 100%;"
              />
              <span class="text-xs text-profissa-text-dim mt-1 leading-relaxed block">
                Montant séquestré auprès du client lors de l'envoi d'un artisan sur site.
              </span>
            </el-form-item>

            <el-form-item label="Taux de commission ProFissa (%)">
              <el-input-number
                v-model="commissionPercent"
                :min="1"
                :max="50"
                :step="0.5"
                :precision="1"
                controls-position="right"
                style="width: 100%;"
              />
              <span class="text-xs text-profissa-text-dim mt-1 leading-relaxed block">
                Pourcentage prélevé sur le devis validé de la prestation (actuel : {{ commissionPercent }}%).
              </span>
            </el-form-item>

            <el-form-item label="Seuil minimal de retrait artisan (FCFA)">
              <el-input-number
                v-model="form.min_withdrawal_amount"
                :min="500"
                :max="100000"
                :step="500"
                controls-position="right"
                style="width: 100%;"
              />
              <span class="text-xs text-profissa-text-dim mt-1 leading-relaxed block">
                Solde minimum requis pour déclencher un virement LigdiCash vers Mobile Money.
              </span>
            </el-form-item>
          </el-form>
        </el-card>

        <!-- Carte Géolocalisation & Dispatch -->
        <el-card class="settings-card !border-profissa-glass-border">
          <template #header>
            <div class="flex items-center gap-2.5">
              <div class="w-8 h-8 rounded-lg bg-profissa-accent/10 border border-profissa-accent/20 flex items-center justify-center text-profissa-accent">
                <el-icon class="text-base"><Location /></el-icon>
              </div>
              <div>
                <span class="font-bold text-sm sm:text-base text-profissa-text-main block">Géolocalisation & Dispatch</span>
                <span class="text-[0.7rem] text-profissa-text-dim block">Rayons de recherche et sécurité PIN</span>
              </div>
            </div>
          </template>

          <el-form label-position="top" :disabled="!canManage" class="space-y-4">
            <el-form-item label="Rayon GPS standard de matching (km)">
              <el-input-number
                v-model="form.matching_hard_cutoff_km"
                :min="1"
                :max="100"
                :step="1"
                :precision="1"
                controls-position="right"
                style="width: 100%;"
              />
              <span class="text-xs text-profissa-text-dim mt-1 leading-relaxed block">
                Distance maximale standard entre un artisan disponible et le lieu d'intervention.
              </span>
            </el-form-item>

            <el-form-item label="Rayon GPS étendu de secours (km)">
              <el-input-number
                v-model="form.matching_fallback_cutoff_km"
                :min="form.matching_hard_cutoff_km"
                :max="150"
                :step="1"
                :precision="1"
                controls-position="right"
                style="width: 100%;"
              />
              <span class="text-xs text-profissa-text-dim mt-1 leading-relaxed block">
                Rayon élargi si aucun artisan n'est détecté dans le rayon standard.
              </span>
            </el-form-item>

            <el-form-item label="Distance maximale de validation PIN (mètres)">
              <el-input-number
                v-model="form.pin_distance_meters"
                :min="20"
                :max="2000"
                :step="10"
                controls-position="right"
                style="width: 100%;"
              />
              <span class="text-xs text-profissa-text-dim mt-1 leading-relaxed block">
                Périmètre maximal d'arrivée artisan autorisant la validation du code PIN client.
              </span>
            </el-form-item>
          </el-form>
        </el-card>
      </div>

      <!-- Ligne 2 : Règles, Support & Maintenance -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <!-- Carte Règles Opérationnelles -->
        <el-card class="settings-card !border-profissa-glass-border">
          <template #header>
            <div class="flex items-center gap-2.5">
              <div class="w-8 h-8 rounded-lg bg-profissa-accent/10 border border-profissa-accent/20 flex items-center justify-center text-profissa-accent">
                <el-icon class="text-base"><Timer /></el-icon>
              </div>
              <div>
                <span class="font-bold text-sm sm:text-base text-profissa-text-main block">Règles Métier</span>
                <span class="text-[0.7rem] text-profissa-text-dim block">Délais de rétractation</span>
              </div>
            </div>
          </template>

          <el-form label-position="top" :disabled="!canManage">
            <el-form-item label="Délai d'annulation sans frais (minutes)">
              <el-input-number
                v-model="form.cancellation_grace_minutes"
                :min="1"
                :max="60"
                :step="1"
                controls-position="right"
                style="width: 100%;"
              />
              <span class="text-xs text-profissa-text-dim mt-1 leading-relaxed block">
                Délai de grâce accordé au client après acceptation pour annuler sans pénalité financière.
              </span>
            </el-form-item>
          </el-form>
        </el-card>

        <!-- Carte Assistance & Coordonnées -->
        <el-card class="settings-card !border-profissa-glass-border">
          <template #header>
            <div class="flex items-center gap-2.5">
              <div class="w-8 h-8 rounded-lg bg-profissa-accent/10 border border-profissa-accent/20 flex items-center justify-center text-profissa-accent">
                <el-icon class="text-base"><PhoneFilled /></el-icon>
              </div>
              <div>
                <span class="font-bold text-sm sm:text-base text-profissa-text-main block">Support & Assistance</span>
                <span class="text-[0.7rem] text-profissa-text-dim block">Contacts des applications mobiles</span>
              </div>
            </div>
          </template>

          <el-form label-position="top" :disabled="!canManage" class="space-y-4">
            <el-form-item label="Téléphone Assistance Directe">
              <el-input
                v-model="form.support_phone"
                placeholder="+226 70 00 00 00"
                clearable
              />
              <span class="text-xs text-profissa-text-dim mt-1 leading-relaxed block">
                Numéro d'appel affiché dans l'assistance in-app.
              </span>
            </el-form-item>

            <el-form-item label="Ligne WhatsApp Support">
              <el-input
                v-model="form.support_whatsapp"
                placeholder="+226 70 00 00 00"
                clearable
              />
              <span class="text-xs text-profissa-text-dim mt-1 leading-relaxed block">
                Numéro ciblé par le bouton de discussion WhatsApp.
              </span>
            </el-form-item>
          </el-form>
        </el-card>

        <!-- Carte Maintenance Système -->
        <el-card class="settings-card md:col-span-2 lg:col-span-1 !border-profissa-glass-border">
          <template #header>
            <div class="flex items-center gap-2.5">
              <div class="w-8 h-8 rounded-lg bg-profissa-accent/10 border border-profissa-accent/20 flex items-center justify-center text-profissa-accent">
                <el-icon class="text-base"><Operation /></el-icon>
              </div>
              <div>
                <span class="font-bold text-sm sm:text-base text-profissa-text-main block">Maintenance</span>
                <span class="text-[0.7rem] text-profissa-text-dim block">Contrôle d'accès général</span>
              </div>
            </div>
          </template>

          <el-form label-position="top" :disabled="!canManage" class="space-y-4">
            <el-form-item label="Mode Maintenance Global">
              <div class="flex items-center justify-between p-3 rounded-xl bg-profissa-glass-bg border border-profissa-glass-border gap-3">
                <span
                  class="text-xs sm:text-sm font-semibold truncate"
                  :class="form.maintenance_mode ? 'text-red-400' : 'text-green-400'"
                >
                  {{ form.maintenance_mode ? 'Maintenance Activée' : 'Plateforme En Service' }}
                </span>
                <el-switch
                  v-model="form.maintenance_mode"
                  active-color="#ef4444"
                  inactive-color="#22c55e"
                  class="shrink-0"
                />
              </div>
            </el-form-item>

            <el-form-item label="Message de maintenance affiché aux apps">
              <el-input
                v-model="form.maintenance_message"
                type="textarea"
                :rows="3"
                placeholder="Ex: ProFissa effectue une maintenance technique planifiée. Retour du service prévu prochainement."
              />
            </el-form-item>
          </el-form>
        </el-card>
      </div>

      <!-- ─── BARRE D'ACTION EN BAS (STICKY & RESPONSIVE) ─── -->
      <div class="settings-bottom-bar mt-8 p-4 sm:p-5 rounded-2xl bg-profissa-surface border border-profissa-glass-border shadow-2xl backdrop-blur-lg sticky bottom-4 z-20">
        <div class="flex flex-col sm:flex-row items-center justify-between gap-4">
          <!-- Statut / Dernière synchro -->
          <div class="flex items-center gap-2.5 text-xs text-profissa-text-dim w-full sm:w-auto justify-center sm:justify-start">
            <span
              class="w-2.5 h-2.5 rounded-full shrink-0"
              :class="isSaving ? 'bg-amber-400 animate-pulse' : (form.maintenance_mode ? 'bg-red-400' : 'bg-green-400')"
            ></span>
            <span v-if="lastUpdated" class="font-mono text-[0.75rem] truncate">
              Synchronisé le {{ lastUpdated }}
            </span>
            <span v-else>Prêt pour enregistrement</span>
          </div>

          <!-- Boutons d'action -->
          <div class="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
            <el-button
              :icon="Refresh"
              :disabled="isLoading || isSaving"
              @click="loadSettings"
              class="w-full sm:w-auto justify-center"
            >
              Réinitialiser
            </el-button>
            <el-button
              type="primary"
              size="large"
              :icon="Check"
              :loading="isSaving"
              :disabled="!canManage || isLoading"
              @click="confirmAndSave"
              class="w-full sm:w-auto justify-center !px-8 font-bold shadow-lg"
            >
              Enregistrer les modifications
            </el-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import {
  Refresh,
  Check,
  Loading,
  Coin,
  Location,
  Timer,
  PhoneFilled,
  Operation,
  Warning,
  WarningFilled,
  Tools,
} from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { fetchPlatformSettings, updatePlatformSettings } from '../services/adminApi'
import { extractApiError } from '../api'
import { useAdminPermissions } from '../composables/useAdminPermissions'

const { hasPermission } = useAdminPermissions()
const canManage = computed(() => hasPermission('settings.manage'))

const isLoading = ref(false)
const isLoaded = ref(false)
const isSaving = ref(false)
const lastUpdated = ref('')

const form = reactive({
  diagnostic_fee: 2000,
  commission_rate: 0.15,
  min_withdrawal_amount: 1000,
  matching_hard_cutoff_km: 30.0,
  matching_fallback_cutoff_km: 50.0,
  pin_distance_meters: 120,
  cancellation_grace_minutes: 5,
  support_phone: '+22670000000',
  support_whatsapp: '+22670000000',
  maintenance_mode: false,
  maintenance_message: null,
})

const commissionPercent = computed({
  get() {
    return Number((form.commission_rate * 100).toFixed(1))
  },
  set(val) {
    form.commission_rate = Number((val / 100).toFixed(4))
  },
})

const loadSettings = async () => {
  isLoading.value = true
  try {
    const data = await fetchPlatformSettings()
    Object.assign(form, {
      diagnostic_fee: data.diagnostic_fee,
      commission_rate: data.commission_rate,
      min_withdrawal_amount: data.min_withdrawal_amount,
      matching_hard_cutoff_km: data.matching_hard_cutoff_km,
      matching_fallback_cutoff_km: data.matching_fallback_cutoff_km,
      pin_distance_meters: data.pin_distance_meters,
      cancellation_grace_minutes: data.cancellation_grace_minutes,
      support_phone: data.support_phone || '',
      support_whatsapp: data.support_whatsapp || '',
      maintenance_mode: !!data.maintenance_mode,
      maintenance_message: data.maintenance_message || '',
    })
    if (data.updated_at) {
      const dt = new Date(data.updated_at)
      lastUpdated.value = dt.toLocaleString('fr-FR')
    }
    isLoaded.value = true
  } catch (err) {
    ElMessage.error(extractApiError(err))
  } finally {
    isLoading.value = false
  }
}

const confirmAndSave = async () => {
  if (form.matching_fallback_cutoff_km < form.matching_hard_cutoff_km) {
    ElMessage.warning('Le rayon étendu de secours doit être supérieur ou égal au rayon standard.')
    return
  }

  let confirmMsg = 'Confirmez-vous l\'enregistrement de ces paramètres ? Ils prendront effet immédiatement.'
  let confirmType = 'info'

  if (form.maintenance_mode) {
    confirmMsg = 'ATTENTION : Le mode maintenance est ACTIVÉ. Toutes les opérations mobiles clients et artisans seront bloquées. Confirmer ?'
    confirmType = 'warning'
  }

  try {
    await ElMessageBox.confirm(confirmMsg, 'Enregistrement des paramètres', {
      confirmButtonText: 'Confirmer',
      cancelButtonText: 'Annuler',
      type: confirmType,
    })
  } catch {
    return
  }

  isSaving.value = true
  try {
    const payload = {
      diagnostic_fee: form.diagnostic_fee,
      commission_rate: form.commission_rate,
      min_withdrawal_amount: form.min_withdrawal_amount,
      matching_hard_cutoff_km: form.matching_hard_cutoff_km,
      matching_fallback_cutoff_km: form.matching_fallback_cutoff_km,
      pin_distance_meters: form.pin_distance_meters,
      cancellation_grace_minutes: form.cancellation_grace_minutes,
      support_phone: form.support_phone.trim(),
      support_whatsapp: form.support_whatsapp.trim(),
      maintenance_mode: form.maintenance_mode,
      maintenance_message: form.maintenance_message?.trim() || null,
    }

    const res = await updatePlatformSettings(payload)
    ElMessage.success('Paramètres de la plateforme mis à jour avec succès.')
    if (res.updated_at) {
      const dt = new Date(res.updated_at)
      lastUpdated.value = dt.toLocaleString('fr-FR')
    }
  } catch (err) {
    ElMessage.error(extractApiError(err))
  } finally {
    isSaving.value = false
  }
}

onMounted(() => {
  loadSettings()
})
</script>

<style scoped>
.settings-card {
  border-radius: var(--radius);
}

.settings-bottom-bar {
  background-color: var(--surface);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
}
</style>
